import { useMemo } from 'react';
import { usePDPData } from '~/features/product-details/context';
import { TranslationKey } from '~/lib/data-contract';
import { Typography, TypographyProps } from '~/shared/components';
import { useCountdown } from '~/shared/hooks/countdown';
import { TimeLeft } from '~/shared/utils/calculate-time-left';
import { useTranslation } from '~/shared/utils/translation';

type CountdownCopy = {
    key: TranslationKey;
    count?: string;
};

const COUNTDOWN_FALLBACKS: Record<string, string> = {
    'pdp.countdown.messageLive': 'Live nu!',
    'pdp.countdown.messageDay': 'Live om {{count}} dag',
    'pdp.countdown.messageDays': 'Live om {{count}} dage',
    'pdp.countdown.messageHours': 'Live om {{count}} timer',
    'pdp.countdown.messageMinutes': 'Live om {{count}} min.',
    'pdp.countdown.messageSeconds': 'Live om {{count}} sek.',
};

/**
 * Ties the number to the unit that follows it with a non-breaking space, so a line break lands
 * before the count rather than between it and its unit.
 */
const keepCountWithUnit = (text: string, count?: string) =>
    count ? text.replace(`${count} `, `${count}\u00a0`) : text;

/**
 * The largest unit with anything left in it, so the bar carries one number instead of four.
 * Only the day form inflects: the design abbreviates the shorter units, and those read the same
 * at any count, which keeps this to six keys for an editor to maintain.
 */
const getCountdownCopy = (timeLeft: TimeLeft, isExpired: boolean): CountdownCopy => {
    if (isExpired) return { key: 'pdp.countdown.messageLive' };

    if (timeLeft.days > 0) {
        return {
            key: `pdp.countdown.message${timeLeft.days === 1 ? 'Day' : 'Days'}`,
            count: String(timeLeft.days),
        };
    }

    if (timeLeft.hours > 0) {
        return { key: 'pdp.countdown.messageHours', count: String(timeLeft.hours) };
    }

    if (timeLeft.minutes > 0) {
        return { key: 'pdp.countdown.messageMinutes', count: String(timeLeft.minutes) };
    }

    return { key: 'pdp.countdown.messageSeconds', count: String(timeLeft.seconds) };
};

type Props = TypographyProps & {
    /** Freezes the tick while the bar is parked, without taking the markup out of the layout. */
    isPaused?: boolean;
};

export const ProductStickyCountdown: React.FC<Props> = ({ className, isPaused, ...rest }) => {
    const { product } = usePDPData();

    if (!product.c_prelaunch) return null;

    return (
        <StickyCountdown
            className={className}
            endDate={product.c_prelaunch}
            isPaused={isPaused}
            {...rest}
        />
    );
};

const StickyCountdown: React.FC<Props & { endDate: string }> = ({ endDate, isPaused, ...rest }) => {
    const { translate } = useTranslation();
    const { timeLeft, isExpired } = useCountdown({ endDate, hydrateSafe: false, isPaused });

    const copy = getCountdownCopy(timeLeft, isExpired);

    // Keyed on the resolved copy rather than the tick, so a day's countdown translates once.
    const text = useMemo(() => {
        const translated = copy.count
            ? translate(copy.key as TranslationKey, { count: copy.count })
            : translate(copy.key as TranslationKey);

        if (translated) return keepCountWithUnit(translated, copy.count);

        const fallback = COUNTDOWN_FALLBACKS[copy.key] ?? '';

        return keepCountWithUnit(
            copy.count ? fallback.replace('{{count}}', copy.count) : fallback,
            copy.count,
        );
    }, [copy.key, copy.count]);

    return (
        <Typography variant="paragraph-S" as="p" {...rest}>
            {text}
        </Typography>
    );
};
